import {
  FormControl,
  FormControlProps,
  FormHelperText,
  FormHelperTextProps,
  FormLabelProps,
  RadioGroup,
  RadioGroupProps,
} from "@mui/material";
import FormControlLabel, {
  FormControlLabelProps,
} from "@mui/material/FormControlLabel";
import Radio, { RadioProps } from "@mui/material/Radio";
import { getAriaDescribedByValue, getKey } from "core/model/utils/strings";
import { EitherOr } from "core/types";
import { WHITE } from "ds_legacy/materials/colors";
import { margin } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14 } from "ds_legacy/materials/typography";
import { CSSProperties, Fragment } from "react";
import {
  FormElementRenderProps,
  FormWatcher,
  isValid,
} from "react-forms-state";
import { useTranslations } from "translations";
import { SubheadingFormLegend } from "../FormComponents/SubheadingFormLegend";
import { getHelperText } from "../Validation";

export type RadioOptionValue = number | string;

export type RadioOptionV2<T extends RadioOptionValue> = EitherOr<
  { label: string },
  { ariaLabel: string },
  {
    ariaDescribedBy?: string;
    checked?: boolean;
    disabled?: boolean;
    subForm?: React.ReactNode;
    subFormStyle?: CSSProperties;
    value: T;
  }
>;

export type RadioGroupV2Props<T extends RadioOptionValue = RadioOptionValue> = {
  defaultValue?: RadioGroupProps["defaultValue"];
  elementName: string;
  formControlLabelSx?: FormControlLabelProps["sx"];
  formControlSx?: FormControlProps["sx"];
  formHelperTextSx?: FormHelperTextProps["sx"];
  formLabelSx?: FormLabelProps["sx"];
  include?: T[] | null;
  isDisabled?: boolean;
  isRequired?: boolean;
  label: string;
  options: Array<RadioOptionV2<T>>;
  radioGroupSx?: RadioGroupProps["sx"];
  radioSx?: RadioProps["sx"];
  row?: RadioGroupProps["row"];
  sideMutation?:
    | ((
        newValue: any,
        mutateElement: (value: any, elementName: string) => void,
      ) => void)
    | undefined;
  withFlatModel?: boolean;
};

export default function RadioGroupV2<
  T extends RadioOptionValue = RadioOptionValue,
>({
  defaultValue,
  elementName,
  formControlLabelSx,
  formControlSx,
  formHelperTextSx,
  formLabelSx,
  include,
  isDisabled,
  isRequired,
  label,
  options,
  radioGroupSx,
  radioSx,
  row,
  sideMutation,
  withFlatModel,
}: RadioGroupV2Props<T>) {
  const translations = useTranslations();
  const legendId = `${elementName}-radio-group-legend`;
  let optionsToShow = options;

  if (include) {
    optionsToShow = options.filter((option) => include.includes(option?.value));
  }

  return (
    <FormElementRenderProps
      elementName={elementName}
      sideMutation={sideMutation}
      withFlatModel={withFlatModel}
    >
      {({ onChange, validation, value }) => {
        const errorTextId = `${elementName}_error_text_id`;
        const hasError = !isValid(validation);
        return (
          <FormControl
            component="fieldset"
            error={hasError}
            sx={{ ...formControlSx }}
          >
            <SubheadingFormLegend
              id={legendId}
              required={isRequired}
              sx={formLabelSx}
            >
              {label}
            </SubheadingFormLegend>
            <RadioGroup
              aria-labelledby={`${legendId} ${
                hasError ? errorTextId : ""
              }`.trim()}
              defaultValue={defaultValue}
              name={elementName}
              row={row}
              sx={{ ...radioGroupSx }}
              value={value}
              onChange={(e, value) =>
                onChange(
                  typeof optionsToShow[0].value === "number"
                    ? Number(value)
                    : value,
                )
              }
            >
              {optionsToShow.map(
                ({ subForm, subFormStyle, ...option }, index) => (
                  <Fragment key={getKey("fragment", option.value, index)}>
                    <FormControlLabel
                      key={getKey("controlLabel", option.value, index)}
                      checked={
                        typeof option.checked === "boolean"
                          ? option.checked
                          : option.value === value
                      }
                      control={
                        <Radio
                          disabled={isDisabled}
                          inputProps={{
                            ...{
                              ["data-testid"]: `radio_${elementName}_${option.value}`,
                            },
                            "aria-describedby": getAriaDescribedByValue([
                              option.ariaDescribedBy,
                              hasError && errorTextId,
                            ]),
                            "aria-invalid": hasError ? true : undefined,
                            "aria-label": option.ariaLabel,
                          }}
                          sx={{
                            ...radioSx,
                            background: WHITE,
                            margin: margin(0, 1, 0, 0),
                          }}
                        />
                      }
                      data-testid={`radio_${elementName}_${option.value}_label`}
                      disabled={option.disabled}
                      label={option.ariaLabel ? undefined : option.label}
                      required={isRequired}
                      sx={{
                        ...formControlLabelSx,
                        "& .MuiFormControlLabel-asterisk": {
                          display: "none",
                        },
                        "& .MuiFormControlLabel-label": {
                          fontSize: FONT_SIZE_14,
                        },
                      }}
                      value={option.value}
                    />
                    {subForm && (
                      <FormWatcher
                        key={getKey("subForm", option.value, index)}
                        watchPath={elementName}
                      >
                        {({ watchedValue }) => {
                          const isSelected = watchedValue === option.value;
                          return (
                            <div
                              style={{
                                display: isSelected ? "block" : "none",
                                ...subFormStyle,
                              }}
                            >
                              {subForm}
                            </div>
                          );
                        }}
                      </FormWatcher>
                    )}
                  </Fragment>
                ),
              )}
            </RadioGroup>
            <FormHelperText
              data-testid={`${elementName}-form-helper-text`}
              error={!isValid(validation)}
              id={errorTextId}
              sx={{ ...formHelperTextSx }}
            >
              {getHelperText({
                hasCustomValidation: true,
                translations,
                validation,
              })}
            </FormHelperText>
          </FormControl>
        );
      }}
    </FormElementRenderProps>
  );
}
